/**
 * Research ▸ Company Lists: every company list in the firm, whichever search
 * made it (prototype board 2) -- the sibling of People Lists. Creating a list
 * happens inside a search (its Company Lists tab), so this page only reads.
 *
 * `?search_id_thrive=<id>&search_name=<display>` narrows the list to one
 * search, exactly as on People Lists (`ImportsPage`): the filter lives in the
 * URL, not in component state, so it survives a refresh and a shared link,
 * and a removable chip explains why the list is short. `search_name` is
 * display-only — the chip's label before any row has loaded.
 *
 * The name filter and the page live in the URL too (`?q=`, `?page=`), as on
 * People Lists' firm page (`urlFilters`), so opening a list and coming back,
 * or a refresh, keeps them. Applying or clearing the search filter drops
 * `page` in the same write: a new filter starts on page 1.
 *
 * "Imported by" (`?owner=`, Trello #831) follows `ImportsPage`: the page
 * starts at Me, or at Everyone when it arrives with a search filter (a
 * search's lists are usually the team's, not the reader's); that starting
 * scope is written into the URL once on arrival, so coming back from a list
 * never silently narrows it; and applying a search filter while scoped to Me
 * widens to Everyone in the same write. Clearing the filter leaves the scope
 * where it is.
 */
import { useMe } from "@nu-assist/shared-fe";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  type CompanyListsClient,
  createCompanyListsClient,
} from "../../clients/companyLists.client";
import { type ImportsClient, createImportsClient } from "../../clients/imports.client";
import { CompanyListsView } from "../../features/company-lists/CompanyListsView";
import { OWNER_PARAM, PAGE_PARAM } from "../../features/company-lists/useCompanyListsFilters";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const defaultClient = createCompanyListsClient({ apiBase: API_BASE });
// The searches typeahead lists Thrive searches through the imports client.
const defaultImportsClient = createImportsClient({ apiBase: API_BASE });

/** Trimmed query-string value, or `null` when absent/blank. */
function readParam(params: URLSearchParams, key: string): string | null {
  return params.get(key)?.trim() || null;
}

export function CompanyListsPage({
  client = defaultClient,
  importsClient = defaultImportsClient,
  meEmailOverride,
}: {
  client?: CompanyListsClient;
  importsClient?: ImportsClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
}) {
  const me = useMe();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");
  const [searchParams, setSearchParams] = useSearchParams();
  const searchIdThrive = readParam(searchParams, "search_id_thrive");
  const searchName = readParam(searchParams, "search_name");

  // Read once on arrival, as `ImportsPage` does: later search-filter changes
  // widen the scope in `applySearchFilter`, not by re-deriving this.
  const [initialOwner] = useState(() => (searchIdThrive ? "all" : "you"));

  // Write the starting scope into the URL once when it arrived without one
  // (`ImportsPage`'s materialising effect, same reason): an absent `?owner=`
  // means Me or Everyone depending on the search filter, which the reader can
  // clear, so a round trip through a list could otherwise narrow the scope.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once by design.
  useEffect(() => {
    if ((searchParams.get(OWNER_PARAM) ?? "").trim()) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(OWNER_PARAM, initialOwner);
        return next;
      },
      { replace: true }
    );
  }, []);

  // `replace`, as on People Lists: a filter is a view of this page, not a new
  // place to go back to.
  function applySearchFilter(searchId: string, name: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        // A search ARRIVING (none before) widens Me to Everyone, as People
        // Lists does; replacing one search with another leaves the scope alone,
        // and so does an explicit recruiter.
        const arriving = !current.get("search_id_thrive")?.trim();
        const scope = current.get(OWNER_PARAM)?.trim() || initialOwner;
        if (arriving && scope === "you") next.set(OWNER_PARAM, "all");
        next.set("search_id_thrive", searchId);
        if (name) next.set("search_name", name);
        else next.delete("search_name");
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true }
    );
  }

  function clearSearchFilter() {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("search_id_thrive");
        next.delete("search_name");
        next.delete(PAGE_PARAM);
        return next;
      },
      { replace: true }
    );
  }

  return (
    <div data-testid="company-lists-page" className="flex flex-col gap-4 p-8">
      <CompanyListsView
        client={client}
        searchId={null}
        urlFilters
        meEmail={meEmail}
        initialOwner={initialOwner}
        searchFilter={{
          importsClient,
          searchIdThrive,
          searchName,
          onApply: applySearchFilter,
          onClear: clearSearchFilter,
        }}
      />
    </div>
  );
}
